'use client';

import { getLastMonthDates, getLastWeekDates, getMonthToDateDates, getYearToDateDates } from '@wolfejs/core/utils/date';
import { Button } from '@wolfejs/ui/Button';
import { CSVDownloadLink } from '@wolfejs/ui/CSVDocument';
import { Form, FormField, Label } from '@wolfejs/ui/Form';
import type { FormSchemaEntry } from '@wolfejs/ui/Form/types';
import { Title } from '@wolfejs/ui/Title';
import { LayoutTypes, UIColorContext, UISizes, UIVariants } from '@wolfejs/ui/types/UI';
import { useState } from 'react';
import { z } from 'zod';
import type { MappedOrder, ReportFormValues } from './types';

export type ReportFormProps = {
  data?: MappedOrder[];
  name: string;
  onSubmit: (values: ReportFormValues) => void;
  defaultValues: ReportFormValues;
};

const schema: FormSchemaEntry[] = [
  {
    name: 'period',
    label: 'Report period',
    type: 'select',
    props: {
      size: UISizes.sm,
      color: UIColorContext.primary,
      fullWidth: true,
      options: [
        { value: 'lastWeek', label: 'Last week' },
        { value: 'lastMonth', label: 'Last month' },
        { value: 'monthToDate', label: 'Month to date' },
        { value: 'yearToDate', label: 'Year to date' },
        { value: 'custom', label: 'Custom' },
      ],
    },
    validation: z.object({
      value: z.string().optional(),
      label: z.string().optional(),
    }),
  },
  {
    name: 'from',
    label: '',
    type: 'datepicker',
    validation: z.coerce.date(),
  },
  {
    name: 'to',
    label: '',
    type: 'datepicker',
    validation: z.coerce.date(),
  },
];

const getNewDates = (period?: string) => {
  switch (period) {
    case 'lastWeek':
      return getLastWeekDates();
    case 'lastMonth':
      return getLastMonthDates();
    case 'monthToDate':
      return getMonthToDateDates();
    case 'yearToDate':
      return getYearToDateDates();
    default:
      return getLastWeekDates();
  }
};

export const ReportForm = ({ name, defaultValues, onSubmit, data }: ReportFormProps) => {
  const [values, setValues] = useState<ReportFormValues>(defaultValues);

  const handleChange = (data: Partial<ReportFormValues>, name: unknown) => {
    const datesChanged = (name === 'from' && values.from != data.from) || (name === 'to' && values.to != data.to);
    const periodChanged = name === 'period';
    setValues({
      ...values,
      ...data,
      ...(datesChanged ? { period: { value: 'custom', label: 'Custom' } } : {}),
      ...(periodChanged ? getNewDates(data.period?.value) : {}),
    });
  };

  return (
    <div className="dark:bg-default-900 flex flex-col gap-4 rounded-lg  bg-white p-4">
      <Title as="h5" className="text-default-900 dark:text-default-500 mb-0 text-base">
        {name}
      </Title>

      <Form<ReportFormValues>
        schema={schema}
        values={values}
        layout={LayoutTypes.horizontal}
        onSubmit={onSubmit}
        noDividers={true}
        onChange={(data, name) => handleChange(data, name)}
        render={() => {
          return (
            <div className="flex flex-col gap-4">
              <div className="flex items-end justify-between gap-3">
                <div className="flex items-end gap-3">
                  <FormField name="period" className="w-1/4" />
                  <div className="flex items-center gap-3">
                    <FormField name="from" className="flex flex-col justify-end" />
                    <Label> to </Label>
                    <FormField name="to" className="flex flex-col justify-end" />
                  </div>
                </div>
                <div className="flex gap-2">
                  {data && (
                    <CSVDownloadLink<Array<MappedOrder>> data={data} fileName={`${name}.csv`}>
                      <Button
                        color={UIColorContext.primary}
                        variant={UIVariants.outline}
                        size={UISizes.sm}
                        type="button"
                      >
                        Export report
                      </Button>
                    </CSVDownloadLink>
                  )}

                  <Button color={UIColorContext.primary} size={UISizes.sm} type="submit">
                    Run report
                  </Button>
                </div>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};
